import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { adminApiService } from "../services/admin-api-service"
import type { AdminListOptions, AdminListResponse } from "../types"

interface UseAdminResourceOptions {
  initialFilters?: Record<string, any>
  autoFetch?: boolean
}

/**
 * Hook genérico para operações CRUD em recursos admin
 */
export function useAdminResource<T>(
  resourceName: string,
  optionsOrFilters?: UseAdminResourceOptions | Record<string, any>
) {
  // Suporta tanto o formato antigo (initialFilters direto) quanto o novo (options object)
  const options: UseAdminResourceOptions =
    optionsOrFilters && "autoFetch" in optionsOrFilters
      ? optionsOrFilters
      : { initialFilters: optionsOrFilters as Record<string, any> | undefined, autoFetch: true }

  const { initialFilters, autoFetch = true } = options
  const [data, setData] = useState<T[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const [pagination, setPagination] = useState<AdminListResponse<T>["pagination"]>()
  const [totalCount, setTotalCount] = useState<number>(0)

  // Usar ref para evitar loops infinitos
  const initialFiltersRef = useRef<string>("{}")
  const currentFilters = useMemo(() => {
    const filtersString = JSON.stringify(initialFilters || {})
    if (filtersString !== initialFiltersRef.current) {
      initialFiltersRef.current = filtersString
    }
    return JSON.parse(initialFiltersRef.current)
  }, [initialFilters])

  const list = useCallback(
    async (additionalFilters: AdminListOptions = {}) => {
      setLoading(true)
      setError(null)

      try {
        const combinedFilters = { ...currentFilters, ...additionalFilters }
        const response = await adminApiService.listResource<T>(resourceName, combinedFilters)
        setData(response.data)
        setPagination(response.pagination)
        setTotalCount(response.total_count || 0)
        return response
      } catch (err) {
        const error = err instanceof Error ? err : new Error(`Failed to fetch ${resourceName}`)
        setError(error)
        throw error
      } finally {
        setLoading(false)
      }
    },
    [resourceName, currentFilters]
  )

  const get = useCallback(
    async (id: string) => {
      setLoading(true)
      setError(null)

      try {
        const response = await adminApiService.getResource<T>(resourceName, id)
        return response.data
      } catch (err) {
        const error =
          err instanceof Error ? err : new Error(`Failed to fetch ${resourceName} ${id}`)
        setError(error)
        throw error
      } finally {
        setLoading(false)
      }
    },
    [resourceName]
  )

  const create = useCallback(
    async (item: Partial<T>) => {
      setLoading(true)
      setError(null)

      try {
        const response = await adminApiService.createResource<T>(resourceName, item)
        setData(prev => [...prev, response.data])
        return response.data
      } catch (err) {
        const error = err instanceof Error ? err : new Error(`Failed to create ${resourceName}`)
        setError(error)
        throw error
      } finally {
        setLoading(false)
      }
    },
    [resourceName]
  )

  const update = useCallback(
    async (id: string, item: Partial<T>) => {
      setLoading(true)
      setError(null)

      try {
        const response = await adminApiService.updateResource<T>(resourceName, id, item)
        setData(prev =>
          prev.map(existing =>
            (existing as any).id === id || (existing as any)._id === id ? response.data : existing
          )
        )
        return response.data
      } catch (err) {
        const error =
          err instanceof Error ? err : new Error(`Failed to update ${resourceName} ${id}`)
        setError(error)
        throw error
      } finally {
        setLoading(false)
      }
    },
    [resourceName]
  )

  const patch = useCallback(
    async (id: string, item: Partial<T>) => {
      setLoading(true)
      setError(null)

      try {
        const response = await adminApiService.patchResource<T>(resourceName, id, item)
        setData(prev =>
          prev.map(existing =>
            (existing as any).id === id || (existing as any)._id === id ? response.data : existing
          )
        )
        return response.data
      } catch (err) {
        const error =
          err instanceof Error ? err : new Error(`Failed to patch ${resourceName} ${id}`)
        setError(error)
        throw error
      } finally {
        setLoading(false)
      }
    },
    [resourceName]
  )

  const remove = useCallback(
    async (id: string) => {
      setLoading(true)
      setError(null)

      try {
        await adminApiService.deleteResource(resourceName, id)
        setData(prev => prev.filter(item => (item as any).id !== id && (item as any)._id !== id))
      } catch (err) {
        const error =
          err instanceof Error ? err : new Error(`Failed to delete ${resourceName} ${id}`)
        setError(error)
        throw error
      } finally {
        setLoading(false)
      }
    },
    [resourceName]
  )

  // Auto-fetch na inicialização com ordenação padrão (pode ser desabilitado)
  const hasAutoFetched = useRef(false)
  useEffect(() => {
    if (autoFetch && !hasAutoFetched.current) {
      hasAutoFetched.current = true
      list({ order: "created_at.desc" })
    }
  }, [autoFetch, list])

  return {
    data,
    loading,
    error,
    pagination,
    totalCount,
    list,
    get,
    create,
    update,
    patch,
    remove,
    refetch: list,
  }
}
